<template>
	<div>
		<app-app-header v-model="indexStore.isHeaderVisible"
						:title="appHeaderData.title"
						:breadcrumbs="appHeaderData.breadcrumbs"
						:image="{path: eventStore.event.avatar_thumbnail}"
						:color="eventStore.colorStyle"
						:to="appHeaderData.to" flat-on-top>

			<btn fab @click="indexStore.share({id: `participation-${eventStore.event._key}_${participationStore.participation._key}`, url: `${$config.public.appUrl}${$route.path}`})">
				<icon :icon="icons.share"/>
			</btn>

			<dropdown v-model="isNavMenuActive" bottom nudge-right :actions="navItems" :menu-title="`${participationStore.participation.display_name}` + (participationStore.participation?.starter_number ? ` (#${participationStore.participation.starter_number})` : '')" class="ml-12 sm:ml-16">
				<btn fab :aria-label="$t('label.menu')">
					<menu-btn :model-value="isNavMenuActive" small :color="$config.public.colors.white"/>
				</btn>
				<template #menu>
					<divider :margin="8"/>

					<div class="px-16 pt-8">

						<template v-if="participationStore.authPermissions.certificates?.length">
							<document-item v-for="certificate in participationStore.authPermissions.certificates" :key="`certificate-${certificate.id}`"
										   @success="url => handleDownload(url, 'participation_certificate', certificate.template.name)"
										   with-error
										   :action="$api('events.participations.certificates.download', [eventStore.event._key, participationStore.participation._key, certificate._key])"
										   :title="certificate.template.name"
										   :description="certificate.template.description" :icon="icons.certificate">
								<template #icon>
									<cdn-image v-if="certificate.template.preview" :size="{ratio: 1/1.4142, width: 24}"
											   :src="certificate.template.preview"/>
								</template>
							</document-item>
						</template>

						<wrapper break-column="sm" stretch>
							<btn v-if="participationStore.editable && eventStore.isOrganizer"
								 :to="`${$config.public.resultasticUrl}/${eventStore.event._key}/participations/${participationStore.participation._key}`"
								 :color="eventStore.colorStyle" exact outlined>
								<span>{{ $t('action.show_in_resultastic') }}</span>
							</btn>
							<btn v-for="(button, buttonKey) in navButtons"
								 :key="`button-${buttonKey}`"
								 :to="button.to"
								 :color="button.color" exact>
								<icon v-if="button.icon" :icon="button.icon"/>
								<span>{{ button.label }}</span>
							</btn>
						</wrapper>
					</div>

				</template>
			</dropdown>

		</app-app-header>

		<app-content>

			<app-header-toolbar v-if="otherParticipationOptions.length > 1 && $device.isMobile" :on-top="!indexStore.isHeaderVisible" :color="eventStore.colorStyle" flat-on-top-mobile>
				<form-select :options="otherParticipationOptions" v-model="selectedParticipationKey"
							 hide-details :border-color="eventStore.colorStyle" :color="eventStore.colorStyle" class="w-full truncate">
					<template v-slot:selected="{ options }">
						<div v-if="participationStore.participation.has_any_multiple_attempts" class="flex justify-between items-center">
							<span class="truncate">{{ options[0].text }}{{ hasMultipleParticipationClasses ? `: ${options[0].group}` : '' }}</span>
							<span class="text-sm text-gray-400 pl-4">{{ options[0].subtitle }}</span>
						</div>
						<div v-else class="flex">
							<span class="truncate">{{ options[0].text }}</span>
						</div>
					</template>
				</form-select>
			</app-header-toolbar>

			<page-content xs-full sm-full :background="eventStore.event.background_color" :has-app-bottom-bar="hasBottomAppBar">
				<lazy-cdn-background-image v-if="eventStore.event.background_image && $device.isDesktop" class="fixed w-screen h-screen top-0 left-0 right-0 bottom-0"
										   :src="eventStore.event.background_image"
										   :ratio="1920/1080" :width="1920" cover immediate/>

				<container xs-full sm-full class="stretch-layout">
						<row tight class="stretch-layout">
							<column v-if="isIndexPage || !$device.isMobile" cols="12" md="4">
								<sticky-content>
									<card xs-tile sm-tile no-spacing :style="!eventStore.event.is_test ? `border-top: 2px solid ${eventStore.colorStyle}` : null">
										<lazy-test-mode-line v-if="eventStore.event.is_test"/>

										<card-text>
											<div class="flex items-center flex-column">
												<div class="relative mb-8">
													<profile-avatar :src="(participationStore.participation.private && !participationStore.authPermissions.can_view) ? null : participationStore.participation.image"
																	:thumbnail-src="(participationStore.participation.private && !participationStore.authPermissions.can_view) || participationStore.participation.image ? null : participationStore.participation.user?.avatar_thumbnail"
																	without-thumbnail
																	:editable="participationStore.authPermissions.is_participant"
																	:action="`/v1/athletes/${participationStore.participation.user_id}/events/${eventStore.event.id}/participations/${participationStore.participation.id}/image`"
																	name="image"
																	:edit-title="t('action.upload_finisher_image')"
																	:size="144" round @updated="handleUploadedImage"/>
													<div v-if="participationStore.participation.starter_number" class="absolute left-0 bottom-0 z-2">
														<chip small># {{ participationStore.participation.starter_number }}</chip>
													</div>

													<nuxt-link v-if="participationStore.participation.club?.avatar_thumbnail" class="absolute right-0 bottom-0 z-2"
															   :to="$web('clubs.show', participationStore.participation.club._key)">
														<avatar :model="participationStore.participation.club" :size="32"/>
													</nuxt-link>

													<div v-if="!participationStore.participation.image && participationStore.authPermissions.is_participant && participationStore.hasFinished" class="overlay rounded-full flex flex-column items-center justify-center p-16 z-1">
														<icon :icon="icons.upload" color="white" class="mb-2" large/>
														<span class="text-xs text-center text-white leading-spaced">{{ t('action.upload_finisher_image') }}</span>
													</div>

												</div>

												<nuxt-link v-if="participationStore.participation.team" :style="{color: eventStore.colorStyle}"
														   class="block text-center text-sub leading-normal mb-4 truncate w-full"
														   :to="{path: $web('events.participations.show', [eventStore.event._key, participationStore.participation._key]), hash: '#team'}">
													{{ participationStore.participation.team.name }}
												</nuxt-link>

												<div class="flex items-center">
													<p class="block font-heading text-heading-color font-bold leading-normal text-xl mb-4 text-center">
														{{ participationStore.authPermissions.display_name ?? participationStore.participation.display_name }}
													</p>
													<tooltip v-if="participationStore.participation.private" :text="$t('text.participation.private')" class="ml-4">
														<icon :icon="icons.anonymous" x-small/>
													</tooltip>
												</div>

												<form-select v-if="!$device.isMobile && otherParticipationOptions.length > 1" :options="otherParticipationOptions" v-model="selectedParticipationKey" hide-details
															 :color="eventStore.colorStyle" class="w-full my-8 rounded" plain :style="{border: `1px solid ${eventStore.colorStyle}`}">
													<template v-slot:selected="{ options }">
														<div v-if="participationStore.participation.has_any_multiple_attempts" class="flex flex-column items-center py-4">
															<span class="text-sub text-center justify-center leading-spaced mb-2">{{ options[0].group }}</span>
															<span class="text-subtext-color text-sm text-center justify-center leading-spaced">{{ options[0].text }} • {{ options[0].subtitle }}</span>
														</div>
														<div v-else class="flex flex-column items-center py-4">
															<span class="text-sub text-center justify-center leading-spaced mb-2">{{ options[0].text }}</span>
															<span class="text-subtext-color text-sm text-center justify-center leading-spaced">{{ options[0].subtitle }}</span>
														</div>
													</template>
												</form-select>
												<template v-else>
													<div v-if="participationStore.participation.stageRaces.length"
														 class="text-subtext-color text-sub text-center justify-center leading-spaced">
														{{ participationStore.participation.stageRaces.map(stageRace => stageRace.name).join(', ') }}
													</div>
													<div class="text-subtext-color text-sub text-center justify-center leading-spaced">
														{{ participationStore.participation.participationClass.name }}
													</div>
												</template>

												<div v-if="participationStore.participation.ageClass" class="text-subtext-color text-sm text-center justify-center leading-wide mt-4">
													{{ participationStore.participation.ageClass.name }}
												</div>
											</div>
										</card-text>

										<div class="none md:block border-t pt-8">
											<tabs vertical center>
												<template v-for="(navItem, navItemKey) in navItems" :key="navItemKey">
													<div v-if="navItem.divider" class="px-24">
														<divider :margin="8"/>
													</div>
													<tab v-else :exact="navItem.exact" :to="navItem.to"
														 :color="eventStore.colorStyle"
														 :replace="navItem.replace">
														{{ navItem.name }}
													</tab>
												</template>
											</tabs>

											<card-text class="pt-0">
												<divider class="mb-16 mt-8"/>

												<wrapper break-column="xl" stretch>

													<template v-if="participationStore.authPermissions.certificates?.length">
														<document-item v-for="certificate in participationStore.authPermissions.certificates" :key="`certificate-${certificate.id}`"
																	   @success="url => handleDownload(url, 'participation_certificate', certificate.template.name)"
																	   with-error
																	   :action="$api('events.participations.certificates.download', [eventStore.event._key, participationStore.participation._key, certificate._key])"
																	   :title="certificate.template.name"
																	   :description="certificate.template.description" :icon="icons.certificate">
															<template #icon>
																<cdn-image v-if="certificate.template.preview" :size="{ratio: 1/1.4142, width: 24}"
																		   :src="certificate.template.preview"/>
															</template>
														</document-item>
													</template>

													<btn v-for="(button, buttonKey) in navButtons"
														 :key="`button-${buttonKey}`"
														 :to="button.to"
														 :color="button.color" exact>
														<icon v-if="button.icon" :icon="button.icon"/>
														<span>{{ button.label }}</span>
													</btn>

												</wrapper>

											</card-text>
										</div>
									</card>
								</sticky-content>
							</column>
							<column cols="12" md="8" class="stretch-layout">
								<div class="stretch-layout overflow-x-hidden lg:m--8 lg:p-8">
									<NuxtPage keep-alive/>
								</div>
							</column>
						</row>

						<client-only>

							<app-bottom-bar v-if="showGenericBottomAppBar" id="app-bottom-bar">
								<div class="flex flex-row items-center justify-between h-full w-full">
									<chip v-if="$device.isDesktop" class="mr-4 min-w-100 w-100" :color="participationStore.participation.status.color">
										{{ participationStore.participation.status.title }}
									</chip>
									<span v-else/>

									<wrapper grow>
										<div class="flex justify-end">
											<like-form :action="`/v1/events/${eventStore.event.id}/participations/${participationStore.participation.id}/likes`"
													   :count="participationStore.participation.likes_count"
													   :liked="participationStore.authPermissions.has_liked"
													   :likes="participationStore.participation.likersSample"
													   :max="5"
													   @show="showLikesModal = true"
													   ref="likeForm"/>

											<tooltip bottom :text="$t('label.comments')">
												<btn @click="showCommentsModal = true" text large class="px-16">
													<icon small force-size :icon="icons.comment"/>
													<span>{{ participationStore.participation.comments_count }}</span>
												</btn>
											</tooltip>
										</div>

										<btn v-if="participationStore.authPermissions.order?.requires_payment_action" large class="text-sm whitespace-normal lg:min-w-240"
											 color="success"
											 :to="$web('orders.pay', participationStore.authPermissions.order._key)">
											<span>{{ $t('action.pay_now') }}</span>
										</btn>
										<btn v-else-if="participationStore.authPermissions.currentAttempt.can_virtual_enter && participationStore.hasEnabledAppTracking"
											 :to="{path: $web('events.participations.show.start', [eventStore.event._key, participationStore.authPermissions.currentAttempt._key]), query: participationStore.hasValidParticipationToken ? {code: participationStore.authPermissions.currentAttempt.token} : {}}" large color="success" class="lg:min-w-240">
											<div v-if="participationStore.authPermissions.currentAttempt.attempt > 1" class="flex flex-column overflow-hidden">
												<span class="text-sm">{{ $t('action.to_participation_mode') }}</span>
												<span class="text-3xs text-left truncate">{{ participationStore.authPermissions.currentAttempt.attempt }}. {{ $t('label.attempt') }}</span>
											</div>
											<span v-else>{{ $t('action.to_participation_mode') }}</span>
											<icon :icon="icons.next"/>
										</btn>
										<btn v-else-if="participationStore.startParticipationFailureMessage" disabled large class="text-sm whitespace-normal lg:min-w-240"
											 color="success">
											{{ participationStore.startParticipationFailureMessage }}
										</btn>
										<btn v-else-if="participationStore.canStartParticipation" @click="startLiveTracking" large color="success" class="lg:min-w-240">
												<span v-if="participationStore.authPermissions.currentAttempt.attempt > 1" class="flex flex-column overflow-hidden">
													<span class="text-sm">{{ $t('action.to_live_tracking') }}</span>
													<span class="text-3xs text-left truncate">{{ participationStore.authPermissions.currentAttempt.attempt }}. {{ $t('label.attempt') }}</span>
												</span>
											<span v-else>{{ $t('action.to_live_tracking') }}</span>
											<icon :icon="icons.next"/>
										</btn>
										<form-btn v-else-if="canCreateAdditionalAttempt && !participationStore.authPermissions.nextStartPrice?.cents"
												  :action="$api('events.participations.attempts.store', [eventStore.event._key, participationStore.participation._key])"
												  :data="{token: participationStore.participationToken}"
												  method="POST"
												  persist-success-loading
												  large color="success" class="lg:min-w-240" @success="handlePurchasedNextStart">
											<span>{{ $t('label.new_attempt') }}</span>
										</form-btn>
										<btn v-else-if="canCreateAdditionalAttempt"
											 :to="{path: $web('events.participations.attempts.create', [eventStore.event._key, participationStore.participation._key]), query: {token: participationStore.participationToken}}"
											 large color="success" class="lg:min-w-240">
											<span>{{ $t('label.new_attempt') }}</span>
										</btn>
										<btn v-else-if="participationStore.authPermissions.can_update_results && (!participationStore.participation.started_at || (!participationStore.participation.time && !participationStore.participation.distance))"
											 @click="participationStore.showUpdateResultsModal()" large color="success">
											<span>{{ $t('action.submit_results') }}</span>
										</btn>
										<btn v-else-if="participationStore.authPermissions.can_update_results && !participationStore.participation.ranks.length"
											 @click="participationStore.showUpdateResultsModal()" large color="success">
											<span>{{ $t('action.submit_ranks') }}</span>
										</btn>
										<btn v-else-if="participationStore.authPermissions.can_rate && !participationStore.authPermissions.has_rated"
											 :to="$web('events.ratings.create', [eventStore.event._key])" large color="success">
											<icon :icon="icons.create_rating"/>
											<span>{{ $t('action.event_rating.create') }}</span>
										</btn>
										<form-btn v-else-if="!participationStore.participation.is_manual && !participationStore.participation.participationClass.is_virtual && !participationStore.hasStarted && !participationStore.isCanceled && !participationStore.authPermissions.order?.requires_payment_action" @success="participationStore.showCheckinCodeModal"
												  method="GET" large :title="$t('label.checkin')" class="lg:min-w-240"
												  :data="{size: 240}" :action="$api('events.participations.code', [eventStore.event._key, participationStore.participation._key])"
												  :color="participationStore.canStartParticipation ? undefined : 'success'">
											<icon :icon="icons.qr_code"/>
											<span>{{ $t('label.checkin') }}</span>
										</form-btn>

										<template v-if="$device.isDesktop">
											<btn large @click="indexStore.share({id: `participation-${participationStore.participation._key}`, url: `${$config.public.appUrl}${$route.path}`})" class="max-w-48 sm:max-w-56">
												<icon :icon="icons.share"/>
											</btn>

											<tooltip v-if="participationStore.editable && eventStore.isOrganizer" :text="$t('action.show_in_resultastic')" top class="max-w-48 sm:max-w-56">
												<btn :to="`${$config.public.resultasticUrl}/${eventStore.event._key}/participations/${participationStore.participation._key}`"
													 :aria-label="$t('action.manage')"
													 class="w-full"
													 icon large
													 target="_blank">
													<icon :icon="icons.manage"/>
												</btn>
											</tooltip>
										</template>
									</wrapper>

								</div>
							</app-bottom-bar>
						</client-only>

				</container>
			</page-content>

			<lazy-modal v-if="!participationStore.participation.is_manual && participationStore.canViewSensitiveInformation" :title="$t('label.checkin_code')" :subtitle="$t('text.participation.checkin_code')" v-model="participationStore.isCheckinCodeModalVisible">
				<div v-html="participationStore.checkinCode" class="w-full h-full flex items-center justify-center"/>
			</lazy-modal>

			<lazy-participation-interactivity-modals :event="eventStore.event" :participation="participationStore.participation"
													 v-model:likes="showLikesModal" v-model:comments="showCommentsModal"/>

			<template v-if="participationStore.authPermissions.can_update_results">
				<lazy-ajax-form-modal v-model="participationStore.isUpdateResultsModalVisible"
									  :form-options="{action: `/v1/athletes/${participationStore.participation.user_id}/events/${eventStore.event.id}/manual-participations/${participationStore.participation.id}`, method: 'PUT', data: participationStore.updateResultsData, notification: 'saved'}"
									  :modal-options="{title: $t('action.submit_results'), buttonBreakColumn: 'xs'}"
									  :submit-text="$t('action.save')"
									  @success="handleUpdatedResults">
					<manual-participation-results-form-inputs :data="participationStore.updateResultsData"
															  :color="eventStore.colorStyle"
															  :age-class="participationStore.participation.participationClass.supports_age_classes ? participationStore.participation.ageClass : undefined"
															  :gender="participationStore.participation.gender"
															  :with-ranks="participationStore.participation.participationClass.ranks_enabled"
															  :participation-class="participationStore.participation.participationClass" edit/>

					<template #footer>
						<btn @click="chooseActivityFile()">
							<icon :icon="icons.upload"/>
							<span>{{ $t('action.submit_from_file') }}</span>
						</btn>
					</template>

				</lazy-ajax-form-modal>

				<lazy-ajax-form-modal v-model="showUploadActivityFileModal"
									  :submit-text="$t('action.submit_from_file')"
									  :modal-options="{title: $t('action.submit_results'), subtitle: $t('text.participation.submit_results_from_gps_track'), buttonBreakColumn: 'xs'}"
									  :form-options="{action: `/v1/events/${eventStore.event.id}/participations/${participationStore.participation.id}/track`, data: uploadActivityFileData, method: 'POST'}" @success="handleUpdatedResults">
					<map-map style="height: 300px" :tile-opacity="0.6" :focus-elements="uploadActivityFileLatLngs" hide-controls>
						<map-polyline :lat-lngs="uploadActivityFileLatLngs" color="#f0820a"/>
					</map-map>

					<template #footer>
						<btn @click="chooseActivityFile()">
							<span>{{ $t('action.choose_other_file') }}</span>
						</btn>
					</template>

				</lazy-ajax-form-modal>

				<input style="display: none" type="file" name="gpx" accept=".gpx,.fit,.tcx" ref="activityFileUploaderElement"
					   @change="handleSelectedActivityFile"/>
			</template>

		</app-content>
	</div>
</template>

<script setup lang="ts">
import {mdiCommentMultiple, mdiQrcode, mdiSquareEditOutline} from '@mdi/js';
import {EventPageType, ParticipationClassRankMode, ParticipationClassType} from "@spoferan/spoferan-ts-core";
import {backIcon, manageIcon, nextIcon, downIcon, helpIcon, anonymousIcon, editIcon, shareIcon, uploadIcon} from "@spoferan/nuxt-spoferan/icons";
import {useEventStore} from "../../../../store/event";
import {useParticipationStore} from "../../../../store/participation";
import {getCountryEmoji} from "../../../../helpers/emoji_helpers";
import {useIndexStore} from "../../../../store";
import {NativeApp} from "@spoferan/nuxt-spoferan/services";

const indexStore = useIndexStore();
const eventStore = useEventStore();
const participationStore = useParticipationStore();
const route = useRoute();
const router = useRouter();
const {t} = useI18n();
const config = useRuntimeConfig();
const {$api, $web, $auth, $date, $echo, $apiFetch, $analytics, $device, $downloadFile} = useNuxtApp();

// TODO: Remove token access for privacy reasons; only kept for REC
participationStore.participationToken = route.query.code ?? route.query.token;

// Refresh permissions on user change as required when user gets authenticated on client side after SSR hydration
watch(() => $auth.token, () => {
	participationStore.refreshAuthPermissions();
}, {
	// Run immediately when token is already set (CSR) or the token will not be set as the user is not logged in
	immediate: !!$auth.token || !$auth.loggedIn
});

await participationStore.refreshParticipation();

const uploadActivityFileLatLngs = ref([]);
const showUploadActivityFileModal = ref(false);
const uploadActivityFileData = ref({});
const activityFileUploaderElement = ref(null);

const showLikesModal = ref(false);
const showCommentsModal = ref(false);

const icons = {
	create_rating: mdiSquareEditOutline,
	support: helpIcon,
	next: nextIcon,
	back: backIcon,
	down: downIcon,
	manage: manageIcon,
	upload: uploadIcon,
	anonymous: anonymousIcon,
	qr_code: mdiQrcode,
	comment: mdiCommentMultiple,
	share: shareIcon
};

const showGenericBottomAppBar = computed(() => {
	return (participationStore.participation && !isOnParticipationPagePath(['start', 'products', 'attempts/create', 'edit']))
});

const hasBottomAppBar = computed(() => {
	return showGenericBottomAppBar.value || isOnParticipationPagePath(['attempts/create'])
});

const hasMultipleParticipationClasses = computed(() => {
	return participationStore.participation?.sameParticipantParticipations.some(participation => participation.participation_class_name !== participationStore.participation.participation_class_name);
});

const otherParticipationOptions = computed(() => {
	if (participationStore.participation?.sameParticipantParticipations.length <= 1) {
		return []
	}

	if (!participationStore.participation.has_any_multiple_attempts) {
		return participationStore.participation.sameParticipantParticipations.map(sameParticipation => {
			return {value: sameParticipation._key.toString(), text: sameParticipation.participation_class_name, subtitle: sameParticipation.description}
		})
	}

	return participationStore.participation.sameParticipantParticipations.map(sameParticipation => {
		return {
			value: sameParticipation._key.toString(),
			text: `${t('label.attempt')} ${sameParticipation.attempt}`,
			group: sameParticipation.participation_class_name,
			subtitle: sameParticipation.description,
		}
	})
});

const isNavMenuActive = ref(false);
const navItems = computed(() => {
	const items: object[] = [];

	// We only want to replace history on nav bar navigation for mobile devices when not on the index page
	// to only have one back navigation step to the index page
	const isOnIndexPage = route.path === $web('events.participations.show', [eventStore.event._key, participationStore.participation._key])
	const replaceHistory = !isOnIndexPage || $device.isDesktop;

	items.push({
		to: $web('events.participations.show.index', [route.params.event, route.params.participation]),
		name: t('label.overview'),
		replace: true,
		exact: true,
	});

	if (participationStore.participation.participationClass && (participationStore.participation.route || participationStore.hasEnabledAppTracking || participationStore.participation.participationClass.live_tracking_enabled)) {
		items.push({
			to: $web('events.participations.show.tracking', [route.params.event, route.params.participation]),
			name: 'Tracking',
			replace: replaceHistory,
		});
	}

	if (participationStore.participation.participationClass && eventStore.isCompetition) {
		items.push({
			to: $web('events.participations.show.results', [route.params.event, route.params.participation]),
			name: t('label.results'),
			replace: replaceHistory,
		});
	}

	if (eventStore.hasActivePage(EventPageType.Map) && participationStore.participation.participationClass.type !== ParticipationClassType.VirtualEvent1) {
		items.push({
			to: $web('events.participations.show.map', [route.params.event, route.params.participation]),
			name: t('label.map'),
			replace: replaceHistory,
		})
	}

	if (participationStore.canViewSensitiveInformation && eventStore.event.managed && participationStore.participation.has_purchasable_products) {
		items.push({
			to: $web('events.participations.show.products', [route.params.event, route.params.participation]),
			name: t('label.participation_products'),
			replace: replaceHistory,
		})
	}

	if (!participationStore.participation.is_manual && participationStore.canViewSensitiveInformation) {
		items.push({
			to: $web('events.participations.show.payment', [route.params.event, route.params.participation]),
			name: t('label.payments'),
			replace: replaceHistory,
		})
	}

	if (participationStore.isParticipantVisiting  && participationStore.participation.has_acquisition_campaigns) {
		items.push({
			to: $web('events.participations.show.campaigns', [route.params.event, route.params.participation]),
			name: t('label.event_acquisition_campaigns'),
			replace: replaceHistory,
		})
	}

	if (participationStore.editable) {
		items.push({
			name: t('action.edit'),
			to: $web('events.participations.edit', [route.params.event, route.params.participation]),
			replace: replaceHistory,
		});
	}

	if ($auth.loggedIn || (resultsPageEnabled || participationsPageEnabled)) {
		items.push({divider: true});

		if ($auth.loggedIn) {
			items.push({
				name: t('label.my_participations'),
				to: $web('my.events.show', [route.params.event]),
			});
		}

		if (resultsPageEnabled || participationsPageEnabled) {
			items.push({
				name: t('label.all_participants'),
				to: resultsPageEnabled ? $web('events.results.index', [eventStore.event._key]) : $web('events.participations.index', [eventStore.event._key]),
				exact: true
			});
		}
	}

	return items;
});

const navButtons = computed(() => {
	const buttons: {to: string, label: string, color?: string, icon?: string}[] = [];

	if (participationStore.authPermissions.can_rate) {
		buttons.push({
			to: $web('events.ratings.create', [eventStore.event._key]),
			color: participationStore.authPermissions.has_rated ? undefined : 'success',
			icon: icons.create_rating,
			label: t('action.event_rating.create'),
		});
	}

	buttons.push({
		to: $web('events.show', [eventStore.event._key]),
		icon: icons.back,
		label: t('action.to_event'),
	});

	return buttons;
});

const isIndexPage = computed(() => {
	return route.path === $web('events.participations.show', [eventStore.event._key, participationStore.participation._key]);
});

const appHeaderData = computed(() => {
	let title = `${participationStore.participation.display_name}` + (participationStore.participation?.starter_number ? ` (#${participationStore.participation.starter_number})` : '');

	const currentNavItem = navItems.value.find((item, itemIndex) => itemIndex > 0 && route.path.startsWith(item.to));
	if (currentNavItem?.name) {
		title = `${title}: ${currentNavItem.name}`;
	}

	return {
		title: title,
		breadcrumbs: [{text: eventStore.event.display_name, to: $web('events.show', eventStore.event._key)}],
		to: $web('events.participations.show', [eventStore.event._key, participationStore.participation._key])
	};
});

const resultsPageEnabled = computed(() => {
	return eventStore.hasActivePage(EventPageType.Results);
});

const participationsPageEnabled = computed(() => {
	return eventStore.hasActivePage(EventPageType.Participations);
});

const canCreateAdditionalAttempt = computed(() => {
	return participationStore.isParticipantOrCreatorVisiting && !!participationStore.authPermissions.nextStartPrice;
});

const selectedParticipationKey = ref(route.params.participation);
watch(selectedParticipationKey, (selectedParticipationKey) => {
	handleSelectedParticipation(selectedParticipationKey);
});

onMounted(() => {
	listenForUpdates(participationStore.participation.id);
})

onBeforeUnmount(() => {
	stopListenForUpdates(participationStore.participation.id);
})

function isOnParticipationPagePath(paths) {
	return paths.some(prefixPath => {
		// A test event might has uppercase letters
		return route.path && route.params.event && route.path.toLowerCase().startsWith($web('events.participations.show', [route.params.event.toLowerCase(), route.params.participation]) + '/' + prefixPath);
	})
}

function getSeoDescription() {
	if (!participationStore.participation) {
		return undefined;
	}

	const descriptionParts = [
		t('label.participation_of_name_at_event', {
			name: participationStore.participation.private ? t('label.anonymous') : participationStore.participation.display_name,
			event: participationStore.participation.participationClass ? participationStore.participation.participationClass.name : eventStore.event.display_name
		}),
		`${t('label.status')}: ${participationStore.participation.status.title}`
	];

	if (participationStore.participation.ranks && participationStore.participation.ranks.length) {
		participationStore.participation.ranks.forEach(rank => {
			if (rank.rank) {
				descriptionParts.push(`${rank.name}: ${rank.rank}. ${t('label.rank')}`)
			}
		})
	}

	if (participationStore.participation.participationClass && participationStore.participation.participationClass.rank_metric && participationStore.participation[participationStore.participation.participationClass.rank_metric] !== null) {
		descriptionParts.push(`${t(`enum.rank_metric.${participationStore.participation.participationClass.rank_metric}.title`)}: ${participationStore.parseRankValue(participationStore.participation[participationStore.participation.participationClass.rank_metric])}`)
	}
	if (participationStore.participation.pace) {
		descriptionParts.push(`${t('label.pace')}: ${participationStore.participation.pace}`)
	}
	if (participationStore.participation.starter_number) {
		descriptionParts.push(`${t('label.starter_number')}: ${participationStore.participation.starter_number}`)
	}
	if (participationStore.participation.country) {
		const emoji = getCountryEmoji(participationStore.participation.country.code);
		descriptionParts.push(t('label.origin') + ': ' + participationStore.participation.country.locale_name + (emoji !== '' ? (' ' + emoji) : ''));
	}

	return descriptionParts.join(' ➤ ');
}

function startLiveTracking() {
	if (NativeApp.isNativeApp()) {
		const nativeParams = {
			event_key: eventStore.event._key,
			event_id: eventStore.event.id,
			participation_id: participationStore.authPermissions.currentAttempt._key,
			auth_token: $auth.bearerToken ?? config.public.apiAuthToken,
			participation_token: participationStore.authPermissions.currentAttempt.token,
			user_id: $auth.user ? $auth.user.id : null
		};

		$analytics.track({
			event: 'submit_results',
			method: 'native',
			model_key: eventStore.event._key,
			model_name: eventStore.event.display_name,
		});

		NativeApp.startVirtualParticipation(nativeParams)
	} else {
		router.push({
			path: $web('events.participations.show.start', [eventStore.event._key, participationStore.authPermissions.currentAttempt._key]),
			query: {code: participationStore.authPermissions.currentAttempt.token}
		});
	}
}

function listenForUpdates(participationId) {
	console.log('Listening for participation updates.');
	$echo.subscribe(`participation.${participationId}`)
		.listen('participation.updated', () => {
			console.log('Participation updated event received.');
			participationStore.refreshParticipation($date().unix());
		}, false).listen('live_ranks.updated', (data) => {
		console.log('Result ranks updated event received.');
		const existingRanks = [...participationStore.participation.ranks];
		data.ranks.forEach(liveRank => {
			const existingRankIndex = existingRanks.findIndex(rank => rank.list_id === liveRank.result_list_id);
			if (existingRankIndex > -1) {
				existingRanks[existingRankIndex].rank = liveRank.rank;
			}
		});

		participationStore.participation.ranks = existingRanks;
	})
}

function stopListenForUpdates(participationId) {
	console.log('Stop listening for participation updates.');
	$echo.unsubscribe(`participation.${participationId}`);
}

function handleSelectedParticipation(participationKey) {
	const participation = participationStore.participation?.sameParticipantParticipations?.find(p => p._key === parseInt(participationKey));
	router.replace({params: {participation: participationKey}, query: participationStore.hasValidParticipationToken ? {code: participation?.token} : {}}).catch(() => {
	}); // Might throw duplicated navigation error
	return;
}

function handlePurchasedNextStart(purchaseData) {
	const order = purchaseData.order;

	// Check if the user needs to pay the order
	// This should not be the case here as only free payments are handled but still required as double check
	if (order && order.requires_payment) {
		router.push($web('orders.pay', [order._key]))
	} else {
		if (purchaseData.participations && purchaseData.participations.length) {
			router.push({
				path: $web('events.participations.show.start', [eventStore.event._key, purchaseData.participations[0]._key]),
				query: {token: purchaseData.participations[0].token}
			})
		} else {
			window.location.reload();
		}
	}
}

function handleUpdatedResults() {
	participationStore.refreshParticipation();
	participationStore.isUpdateResultsModalVisible = false;
	showUploadActivityFileModal.value = false;
}

function handleDownload(url, type, label = null) {
	$analytics.track({
		event: 'download',
		model_type_name: type,
		model_name: label,
		model_key: participationStore.participation._key,
		url: url
	});

	$downloadFile(url);
}

function chooseActivityFile() {
	activityFileUploaderElement.value.click();
	participationStore.isUpdateResultsModalVisible = false;
}

function handleSelectedActivityFile(event) {
	const files = event.target.files;
	if (files && files[0]) {
		previewActivityFileForUpload(files[0]);
	}
}

function previewActivityFileForUpload(file: string|File) {
	$apiFetch('/v1/activity-files/preview', {
		method: 'POST',
		params: {
			file: file
		}
	}).then(data => {
		uploadActivityFileData.value.file = file;
		uploadActivityFileLatLngs.value = data.data.polyline;
		showUploadActivityFileModal.value = true;
	}).finally(() => {

		// Clear the uploader input, so we can upload the same file twice
		activityFileUploaderElement.value.value = '';
	})
}

function handleUploadedImage(data) {
	participationStore.participation.image = data.image;
}

useMeta({
	title: eventStore.buildPageTitle(t('view.events.participations.show.index.title', {
		event: eventStore.event.display_name,
		participation_class: participationStore.participation?.participationClass ? participationStore.participation.participationClass.name : '',
		user: participationStore.participation.display_name
	})) as string,
	description: getSeoDescription(),
	noIndex: true
})
</script>

